import { Dialog, DialogContent, DialogTitle, SearchInput } from '@dfh-corp-sites/ui'
import { useLocation } from '@reach/router'
import { navigate } from 'gatsby'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import useResourceLibrarySearch from '../../../hooks/useResourceLibrarySearch'
import ProtocolModal from '../../library/protocols/ProtocolModal'
import Spinner from '../../Spinner'
import { LibraryHeader } from './LibraryHeader'
import ResourceFilters from './ResourceFilters'
import ResourceList from './ResourceList'
import ResourcesLoadMore from './ResourcesLoadMore'

import downArrow from '../../../img/icons/down-arrow.svg'

const Library = ({ className }) => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const initialQuery = params.get('query') || ''
  const [inputValue, setInputValue] = useState(initialQuery)
  const [searchValue, setSearchValue] = useState(initialQuery)
  const [protocolModalOpen, setProtocolModalOpen] = useState(false)
  const [selectedProtocolSlug, setSelectedProtocolSlug] = useState(null)

  const {
    data,
    loading,
    isProfessional,
    searchOptions,
    actions: { fetchNextSlice },
    hasNextPage,
  } = useResourceLibrarySearch(searchValue)

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(inputValue)
    }, 900)

    return () => clearTimeout(handler)
  }, [inputValue])

  useEffect(() => {
    const path = location.pathname
    const protocolMatch = path.match(/\/protocol\/([^/]+)$/)
    if (protocolMatch) {
      setSelectedProtocolSlug(decodeURIComponent(protocolMatch[1]))
      setProtocolModalOpen(true)
    } else {
      setProtocolModalOpen(false)
      setSelectedProtocolSlug(null)
    }
  }, [location.pathname])

  const handleSortToggle = () => {
    const currentSort = params.get('sort')
    if (!currentSort) {
      params.set('sort', 'desc')
    } else {
      params.set('sort', currentSort === 'asc' ? 'desc' : 'asc')
    }
    navigate(`${location.pathname}?${params.toString()}`)
  }

  const handleSearch = (value) => {
    const trimmedValue = value?.trim()
    if (trimmedValue) {
      params.set('query', trimmedValue)
    } else {
      params.delete('query')
    }
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`)
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    if (newValue === '') {
      handleSearch(newValue)
    }
  }
  const currentSort = new URLSearchParams(location.search).get('sort') || 'asc'

  const handleProtocolClick = (slug) => {
    setSelectedProtocolSlug(slug)
    setProtocolModalOpen(true)
  }

  const handleModalChange = (open) => {
    setProtocolModalOpen(open)
    if (!open) {
      navigate('/research-education/library?type=protocol')
    }
  }

  return (
    <div className={className}>
      <LibraryHeader />
      {loading ? (
        <div className="spinner-loading">
          <Spinner loading />
        </div>
      ) : (
        <>
          {data && (
            <div className="flex flex-col md:flex-row border-t border-rule-grey">
              <ResourceFilters
                libraryType={isProfessional ? 'professional' : 'public'}
                meta={data.meta}
                searchOptions={searchOptions}
                className="md:w-1/4 min-w-[250px]"
              />
              <div className="flex-1 border-b border-rule-grey">
                <div className="md:flex items-center border-b border-l border-rule-grey md:h-[62px] xl:h-[82px]">
                  <SearchInput
                    autoFocus
                    placeholder="What can we help you find?"
                    containerClassName="px-2.5 py-3.5 md:py-0 h-full flex items-center md:px-8 border-b md:border-b-0 border-r border-rule-grey w-full md:w-1/2 xl:w-[65%]"
                    className="py-3"
                    variant="narrow"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearch(inputValue)
                    }}
                  />
                  <div className="flex items-center md:block md:pl-10 md:pr-5 h-[45px] md:h-auto w-full md:w-1/2 xl:w-[35%]">
                    <button
                      className="flex gap-3.5 items-center justify-center md:justify-start cursor-pointer w-full md:w-auto"
                      onClick={handleSortToggle}
                    >
                      <div>
                        Sort from
                        <span className="font-bold"> A to Z</span>
                      </div>
                      <img
                        src={downArrow}
                        alt="Arrow"
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${currentSort === 'desc' ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                </div>
                <ResourceList
                  libraryType={isProfessional ? 'professional' : 'public'}
                  searchOptions={searchOptions}
                  meta={data.meta}
                  resources={data.resources}
                  onProtocolClick={handleProtocolClick}
                />
                <ResourcesLoadMore
                  loading={loading}
                  actions={{ fetchNextSlice }}
                  hasNextPage={hasNextPage}
                />
              </div>
            </div>
          )}
          <Dialog open={protocolModalOpen} onOpenChange={handleModalChange}>
            <div className="hidden">
              <DialogTitle>Protocol</DialogTitle>
            </div>
            <DialogContent
              closeSize="lg"
              className="mt-12"
              aria-describedby="protocol-modal-description"
            >
              <ProtocolModal slug={selectedProtocolSlug} onOpenChange={handleModalChange} />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  )
}

Library.propTypes = {
  className: PropTypes.string,
}

export default Library
